import {useMaterialStorageDataQuery} from '@domain/material-storage'
import {useCurrentScheduleQuery, useStockDevelopmentQuery} from '@domain/schedule'
import {
  getListOfDays,
  MomentRange,
  usePlanningChartStartEnd
} from '@widgets/planning-schedule-chart'
import {StockDevelopmentChartContent, StockDevelopmentChartSkeleton} from '@widgets/stock-card'
import {useMemo} from 'react'

import {useSelectedOptimizerPlan} from '../../../providers'

type OptimizerStockLevelMaterialDetailChartContentProps = {
  plantCode: string
  chartRef: React.RefObject<HTMLDivElement>
  width: number
  materialId: number
}

export const OptimizerStockLevelMaterialDetailChartContent = ({
  plantCode,
  chartRef,
  materialId,
  width
}: OptimizerStockLevelMaterialDetailChartContentProps) => {
  const {startOfPlan, endOfPlan, startOfChart, endOfChart} = usePlanningChartStartEnd()
  const planRange = [startOfPlan, endOfPlan] as MomentRange
  const chartRange = [startOfChart, endOfChart] as MomentRange

  const {selectedInTableScheduleId} = useSelectedOptimizerPlan()

  const {data: stockDevelopmentSelectedPlan, isLoading: isStockDevelopmentLoading} =
    useStockDevelopmentQuery({
      materialIds: [materialId],
      scheduleId: selectedInTableScheduleId,
      timeframe: planRange
    })
  const {data: manualSchedule} = useCurrentScheduleQuery({plantId: plantCode, range: chartRange})

  const isOptimizedPlanSelected = selectedInTableScheduleId !== undefined

  const {data: stockDevelopmentProductionPlan} = useStockDevelopmentQuery({
    materialIds: isOptimizedPlanSelected && manualSchedule ? [materialId] : [],
    scheduleId: manualSchedule?.scheduleId,
    timeframe: planRange
  })
  const {data: storageData} = useMaterialStorageDataQuery({materialId})

  const listOfDays = getListOfDays({start: chartRange[0], end: chartRange[1], additionalDays: 1})

  const shouldShowChartSkeleton =
    !listOfDays || isStockDevelopmentLoading || !stockDevelopmentSelectedPlan

  const minTargetLevels = useMemo(() => {
    return (
      storageData && {
        week: storageData.minTargetLevel,
        weekend: storageData.minTargetLevelWeekend
      }
    )
  }, [storageData])

  return shouldShowChartSkeleton ? (
    <StockDevelopmentChartSkeleton />
  ) : (
    <StockDevelopmentChartContent
      listOfDays={listOfDays}
      stockData={stockDevelopmentSelectedPlan[materialId]}
      stockDataSecondary={
        isOptimizedPlanSelected && stockDevelopmentProductionPlan
          ? stockDevelopmentProductionPlan[materialId]
          : undefined
      }
      minTargetLevels={minTargetLevels}
      deadStockLevel={storageData?.deadStockLevel}
      storageCapacity={storageData?.storageCapacity}
      width={width}
      chartRef={chartRef}
    />
  )
}
